import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useMsal } from '@azure/msal-react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChatIcon, LoaderIcon } from '../Components/Global/Icons';
import { getprojectbyidforuser, getProjectByTestId, getprojectforuser } from '../Services/PtaasApiService';
import { FadeAlert } from '../Components/Global/Transitions';
import Tests from '../Models/Tests';
import ProjectPreReqs from '../Components/Overview/ProjectPreReqs';
import ProjectOverview from '../Components/Overview/ProjectOverview';
import ProjectTestTypes from '../Components/Overview/ProjectTestTypes';
import ProjectCommercials from '../Components/Overview/ProjectCommercials';
import Project from '../Models/Project';
import { isAccessAllowed, UserRole } from '../Utilities/AccessControl';
import { useUser } from '../Components/Global/RoleContext';
import UsersWidget from '../Components/Users/UsersWidget';



const ProjectOverviewPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const [test, setTest] = useState<Tests>();
  const [baseTest, setBaseTest] = useState<Tests>();
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [projectdata, setProjectData] = useState<Project>();
  const { myApplicationRole } = useUser();
  // Single source of truth for the access decision so the render-level check and the
  // effect-level check below can never derive different answers from the same role data.
  const resolveRole = useCallback(
    (t?: Tests): UserRole | null =>
      myApplicationRole?.find(role => role?.id === t?.ticketno)?.role ?? null,
    [myApplicationRole]
  );
  const canAccess = isAccessAllowed(resolveRole(test));
  const status = test?.status?.trim() ?? '';
  const testTypes = test?.testType ?? [];
  const project = test?.projectName ?? '';
  const assignments = test?.assignment ?? [];




  // Role-independent base load: fetch the project + the user's matching test ONCE.
  // getProjectByTestId / getprojectforuser do NOT depend on the role, so they must not
  // re-run when myApplicationRole resolves (that was causing a duplicate reload).
  const fetchProject = useCallback(async (): Promise<void> => {
    if (!accounts || accounts.length === 0 || !id) {
      // Nothing to fetch (no account yet / no route id) — stop the spinner instead
      // of leaving <LoaderIcon /> up indefinitely.
      setLoading(false);
      return;
    }

    setLoading(true);
    setFetchError(false);

    try {
      const returnedProject = await getProjectByTestId(instance, accounts[0], id);
      setProjectData(returnedProject);

      const returnedTests = await getprojectforuser(instance, accounts[0]);
      const foundTest = returnedTests?.find(t => t.id === id);

      if (!foundTest) {
        setFetchError(true);
        setBaseTest(undefined);
        setLoading(false);
      } else {
        setBaseTest(foundTest);
      }
    } catch (error) {
      console.error("Error fetching project:", error);
      setFetchError(true);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts[0]?.homeAccountId, instance, id]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  // Role-dependent tail: once the base test is loaded (and whenever the role resolves),
  // decide access and upgrade to the full privileged test if allowed. Only this step
  // re-runs on role change — the base fetches above do not.
  useEffect(() => {
    let cancelled = false;

    const applyAccess = async (): Promise<void> => {
      if (!baseTest || !accounts || accounts.length === 0 || !id) return;

      const hasAccess = isAccessAllowed(resolveRole(baseTest));

      try {
        if (hasAccess) {
          const fullTest = await getprojectbyidforuser(instance, accounts[0], id);
          if (!cancelled) setTest(fullTest);
        } else if (!cancelled) {
          setTest(baseTest);
        }
      } catch (error) {
        console.error("Error fetching project:", error);
        if (!cancelled) setFetchError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    applyAccess();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseTest, myApplicationRole, accounts[0]?.homeAccountId, instance, id]);
  const handleNavigate = async () => {
    const numericId = test?.ticketno?.replace(/\D/g, "");
    // at end navigate to 
    navigate(`/messaging?id=${numericId}`);

  };

  return (loading ? <LoaderIcon /> : (

    <>
      <Row className='mb-4 pb-3'>
        <Col sm={8} as='h1'>{project}</Col>
        <Col sm={4} className='text-end '>
          <Button variant='secondary' onClick={() => handleNavigate()}><ChatIcon />Messaging</Button>
        </Col>
      </Row>
      <Row>
        <Col xl={8}>
          <FadeAlert show={fetchError} dismissFunction={() => setFetchError(false)} />
          <ProjectOverview test={test} testTypes={testTypes} projectdata={projectdata} />
        </Col>
        <Col md={10} lg={8} xl={4}>
          {['Quote Requested', 'Quote Available'].includes(status) ? <ProjectCommercials isdisable={!canAccess} status={status} /> : null}
          {canAccess? <ProjectPreReqs assignments={assignments} isdisable={!canAccess} test={test} />:null}
          <ProjectTestTypes test={test} testTypes={testTypes} isDisabled canAccess={!canAccess} OnProjectTestTypeChange={fetchProject} />
          {['Quote Requested', 'Quote Available'].includes(status) ? null : <ProjectCommercials isdisable={!canAccess} status={status} />}
         {canAccess? <UsersWidget isdisable id={Number(test?.ticketno?.slice(6))} />:null}
        </Col>
      </Row>
    </>
  ));
};

export default ProjectOverviewPage;


